import * as React from "react";
import Progress from "./Progress";
import Filters from "./Filters";

import "../taskpane.css";
import Questionnaire from "./Questionnaire";
import { useSelector } from "react-redux";
import {
  clearSession,
  markAuthChecked,
  selectAccessDenied,
  selectAuthenticated,
  selectAuthChecked,
  selectIdToken,
  selectRefreshToken,
  updateTokens,
} from "../slices/authSlice";
import Login from "./SSOLogin";
import AccessDenied from "./AccessDenied";
import { useAppDispatch } from "../store";
import { SSOAuthApi } from "../api/sso-auth-api";

/* global require */

export interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
}

export default function App({ title, isOfficeInitialized }: AppProps) {
  const [, setOppNo] = React.useState<string>("");
  const [bid, setBid] = React.useState<string>("");
  const [tab, setTab] = React.useState<string>("");
  const [bidDetails, setBidDetails] = React.useState<any>(null);

  const dispatch = useAppDispatch();
  const authenticated = useSelector(selectAuthenticated);
  const authChecked = useSelector(selectAuthChecked);
  const accessDenied = useSelector(selectAccessDenied);
  const idToken = useSelector(selectIdToken);
  const refreshToken = useSelector(selectRefreshToken);

  React.useEffect(() => {
    if (authChecked || !idToken) return;

    const restoreSession = async (): Promise<void> => {
      try {
        const validation = await dispatch(SSOAuthApi.endpoints.validate.initiate(idToken)).unwrap();
        if (validation.valid) {
          dispatch(markAuthChecked());
          return;
        }
        if (!refreshToken) throw new Error("No refresh token");
        const refreshed = await dispatch(SSOAuthApi.endpoints.refresh.initiate(refreshToken)).unwrap();
        dispatch(updateTokens(refreshed));
      } catch (_error) {
        dispatch(clearSession());
      }
    };

    void restoreSession();
  }, [authChecked, dispatch, idToken, refreshToken]);

  const handleOppNumberChange = React.useCallback((newOppNumber: string, newBid: string): void => {
    setOppNo(newOppNumber);
    setBid(newBid);
    setTab("");
  }, []);

  const handleBidChange = React.useCallback(
    (newBid: string): void => {
      if (bid !== newBid) {
        setBid(newBid);
        setTab("");
        setBidDetails(null);
      }
    },
    [bid]
  );

  const handleTabChange = React.useCallback((newTab: string): void => {
    setTab(newTab);
  }, []);

  const handleBidDetailsChange = React.useCallback((newBidDetails: any): void => {
    setBidDetails(newBidDetails);
  }, []);

  if (!authChecked) {
    return (
      <Progress
        title={title}
        logo={require("./../../../assets/logo-filled.png")}
        message="Restoring your SDA session..."
      />
    );
  }

  if (accessDenied) {
    return <AccessDenied isOfficeInitialized={isOfficeInitialized} />;
  }

  if (!authenticated) {
    return <Login isOfficeInitialized={isOfficeInitialized} />;
  }

  if (!isOfficeInitialized) {
    return (
      <Progress
        title={title}
        logo={require("./../../../assets/logo-filled.png")}
        message="Please sideload your addin to see app body."
      />
    );
  }

  return (
    <div id="app">
      <Filters
        onOppNumberChange={handleOppNumberChange}
        onBidChange={handleBidChange}
        onTabChange={handleTabChange}
        onBidDetailsChange={handleBidDetailsChange}
        bid={bid}
        tab={tab}
      />
      <Questionnaire tab={tab} bidDetails={bidDetails} />
    </div>
  );
}
